import { AvailabilitySearchQuery, OptionStringValue } from 'types';
import { ChronoUnit, LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { SearchQueryStringParams } from 'components/search/utils';
import { SearchTicketGroupWithSeatingAreas } from 'components/www/availability-search/AvailabilitySearchResultSimple';
import { consumer, core } from '@tock/coretypes';
import { getDefaultDateTimeChoice } from 'utils/timeUtils';
import { isArray, slice, sortBy, toNumber } from 'es-toolkit/compat';
import protos from 'utils/consumerProto';

const { ExclusivityRestriction } = protos;
const DEFAULT_PARTY_SIZE = 2;

export interface ClosestTicketGroupOptions {
  timeSearched: LocalTime;
  dateSearched: LocalDate;
  limit?: number;
}

export function getClosestTicketGroups(
  availabilities: consumer.SearchTicketGroup[] | SearchTicketGroupWithSeatingAreas[],
  { dateSearched, timeSearched, limit = 3 }: ClosestTicketGroupOptions
) {
  const searchedDateTime = dateSearched.atTime(timeSearched);
  const sortedByTime = sortBy(
    availabilities,
    (availability: consumer.SearchTicketGroup | SearchTicketGroupWithSeatingAreas) => {
      return Math.abs(
        searchedDateTime.until(
          LocalDateTime.parse(availability.availableDateTime),
          ChronoUnit.MINUTES
        )
      );
    }
  );
  return sortBy(slice(sortedByTime, 0, limit), (availability) => {
    return LocalDateTime.MIN.until(
      LocalDateTime.parse(availability.availableDateTime),
      ChronoUnit.MINUTES
    );
  });
}

export interface DefaultSearchQueryStringParams {
  date?: string;
  dateEnd?: string;
  size?: string;
  time?: string;
  type?: consumer.OfferingSearchType | consumer.ExclusivityRestriction;
  city?: string;
  q?: string | string[];
  cuisines?: string | string[];
  businessGroupId?: number;
  userTimeZone?: string;
  displayTerm?: string;
  latlng?: string;
}

export interface CollectionParams {
  collectionDate?: string;
  collectionDateEnd?: number;
  collectionId?: number;
  collectionOfferingSearchType?: consumer.OfferingSearchType;
}

export function setDefaultSearchQueryParams(
  {
    date,
    dateEnd,
    size,
    time,
    type,
    city,
    q,
    businessGroupId,
    latlng,
    cuisines,
    experienceTagAll,
    experienceTagDineIn,
    experienceTagToGo,
    experienceTagCampaign,
    experienceTagSeatingArea,
    priceRange,
  }: SearchQueryStringParams,
  timeZone: string,
  {
    collectionId,
    collectionDate,
    collectionDateEnd,
    collectionOfferingSearchType,
  }: CollectionParams = {
    collectionDate: undefined,
    collectionDateEnd: undefined,
  }
): AvailabilitySearchQuery {
  const defaultDateTime = getDefaultDateTimeChoice(LocalDateTime.now());

  // for this, we prioritize anything from the query string first, then collection, then absolute defaulting.
  const dateWithDefault = date
    ? LocalDate.parse(date)
    : collectionDate
      ? LocalDate.parse(collectionDate)
      : defaultDateTime.toLocalDate();

  const timeWithDefault = time ? LocalTime.parse(time) : defaultDateTime.toLocalTime();

  // Collections pages shouldn't default to a party size, and should leave party size refinement up to the user.
  // This ensures that all businesses relevant to a collection (e.g., a togo experience with only a party
  // size option of 1), remain visible upon initial load
  const sizeRequired = !collectionId && !!type && type !== 'EVENT';
  const sizeWithDefault = size ? toNumber(size) : sizeRequired ? 2 : undefined;

  // for this, we prioritize anything from the query string first, then collection, then absolute defaulting.
  const dateEndDateTime = dateEnd
    ? LocalDate.parse(dateEnd)
    : // If collection has a set length, and there is no date specified by the user (implying that the collection
      // date range has not been changed from the default / set range), show the default end date. If a user selects
      // a date, ignore the range and just show availability for that date, per standard search results behavior
      collectionDateEnd && !date
      ? dateWithDefault.plusDays(collectionDateEnd)
      : undefined;

  const offeringSearchTypeWithDefault = type ?? collectionOfferingSearchType ?? 'ALL_EXPERIENCES';

  const parsedBusinessGroupId = businessGroupId ? Number(businessGroupId) : undefined;

  const checkForArray = (check: string | string[] | undefined) =>
    isArray(check) ? check : check ? [check] : [];

  return {
    date: dateWithDefault,
    dateEnd: dateEndDateTime,
    time: timeWithDefault,
    size: sizeWithDefault,
    type: offeringSearchTypeWithDefault,
    city,
    q,
    latlng,
    businessGroupId: parsedBusinessGroupId,
    userTimeZone: timeZone,
    cuisines: checkForArray(cuisines),
    experienceTagAll: checkForArray(experienceTagAll),
    experienceTagDineIn: checkForArray(experienceTagDineIn),
    experienceTagToGo: checkForArray(experienceTagToGo),
    experienceTagCampaign: checkForArray(experienceTagCampaign),
    experienceTagSeatingArea: checkForArray(experienceTagSeatingArea),
    priceRange,
  };
}

export function isExclusivityRestriction(s: string): s is consumer.ExclusivityRestriction {
  return Object.prototype.hasOwnProperty.call(ExclusivityRestriction, s);
}

export function isQueryTimeValid(query: AvailabilitySearchQuery): boolean {
  const sizeTimeNotRequired = query.type === 'EVENT';
  if (!(query.date && (query.time || sizeTimeNotRequired) && (query.size || sizeTimeNotRequired))) {
    return false;
  }
  const queryDateTime = query.date.atTime(query.time);
  const queryDateEndTime = query.dateEnd ? query.dateEnd.atTime(query.time) : undefined;
  const isNowBeforeDateTime = LocalDateTime.now().isBefore(queryDateTime);
  const isNowBeforeDateEndTime = queryDateEndTime
    ? LocalDateTime.now().isBefore(queryDateEndTime)
    : false;
  const isNowBeforeDate = LocalDate.now().isBefore(query.date);
  const isNowBeforeDateEnd = query.dateEnd ? LocalDate.now().isBefore(query.dateEnd) : false;
  if (sizeTimeNotRequired && (isNowBeforeDate || isNowBeforeDateEnd)) {
    return true;
  } else if (isNowBeforeDateEndTime || isNowBeforeDateTime) {
    return true;
  }
  return false;
}

export function generateCityEntry(city: core.MetroArea): OptionStringValue {
  return {
    key: city.slug,
    value: city.slug,
    label: city.name,
  };
}

export function defaultMissingParams(
  flatSearchQuery: AvailabilitySearchQuery,
  params: Partial<SearchQueryStringParams>,
  queryStringParams: SearchQueryStringParams
): SearchQueryStringParams {
  let newParams = { ...params };
  if (queryStringParams?.type === 'EVENT' && newParams.type !== 'EVENT') {
    //if any params are undefined but required, define it.
    //when Events is selected size and time is undefined
    newParams = {
      ...newParams,
      size: flatSearchQuery?.size
        ? flatSearchQuery?.size.toString()
        : DEFAULT_PARTY_SIZE.toString(),
      time: getDefaultDateTimeChoice(LocalDateTime.now()).toLocalTime().toString(),
    };
  }
  return newParams;
}
